import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import {
  FiHome,
  FiClipboard,
  FiUsers,
  FiAlertCircle,
  FiGrid,
  FiX,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiSettings,
  FiCreditCard,
  FiCalendar,
  FiBarChart2,
  FiClock,
  FiMail
} from 'react-icons/fi';
import {
  canManageUsers,
  canManageRooms,
  canViewIncidents,
  canManageEstablishments,
  canManageCommonSpaces,
  canCreateOrders,
  canManageSubscription,
  canManageEstablishmentSettings,
  canViewReports,
  canManageTimeEntries
} from '../../utils/helpers';
import { USER_ROLES } from '../../utils/constants';
import clsx from 'clsx';
import ConfirmDialog from '../common/ConfirmDialog';

const Sidebar = ({ isOpen, onClose, onCollapseChange }) => {
  const { user, logout } = useAuth();
  const { isMobile, hasFeature } = useApp();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const handleToggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (onCollapseChange) {
      onCollapseChange(newCollapsedState);
    }
  };

  const isEffectivelyCollapsed = !isMobile && isCollapsed && !isHovered;

  const navigationItems = [
    // Platform Admin items
    {
      name: 'Admin Dashboard',
      href: '/admin/dashboard',
      icon: FiHome,
      show: user?.role === USER_ROLES.PLATFORM_ADMIN,
    },
    {
      name: 'Gestión de Establecimientos',
      href: '/admin/establishments',
      icon: FiGrid,
      show: canManageEstablishments(user?.role),
    },
    {
      name: 'Gestión de Planes',
      href: '/subscription-plans',
      icon: FiCreditCard,
      show: [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.SALES].includes(user?.role),
    },
    {
      name: 'Solicitudes de Demo',
      href: '/admin/demo-requests',
      icon: FiMail,
      show: [USER_ROLES.PLATFORM_ADMIN, USER_ROLES.SALES].includes(user?.role),
    },
    // Cleaning Staff item
    {
      name: 'Órdenes de Limpieza',
      href: '/orders/mobile',
      icon: FiClipboard,
      show: user?.role === USER_ROLES.CLEANING_STAFF,
    },
    // Establishment Admin items (nuevo orden)
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: FiHome,
      show: ![USER_ROLES.PLATFORM_ADMIN, USER_ROLES.CLEANING_STAFF, USER_ROLES.SALES].includes(user?.role),
    },
    {
      name: 'Órdenes de Limpieza',
      href: '/orders/desktop',
      icon: FiClipboard,
      show: canCreateOrders(user?.role) && user?.role !== USER_ROLES.SALES && user?.role !== USER_ROLES.CLEANING_STAFF,
    },
    {
      name: 'Incidencias',
      href: '/incidents',
      icon: FiAlertCircle,
      show: canViewIncidents(user?.role) && hasFeature('incident_management') && user?.role !== USER_ROLES.SALES,
    },
    {
      name: 'Cuadrantes',
      href: '/schedules',
      icon: FiCalendar,
      show: canManageCommonSpaces(user?.role) && user?.role !== USER_ROLES.SALES,
    },
    {
      name: 'Registros de Jornada',
      href: '/time-entries',
      icon: FiClock,
      show: canManageTimeEntries(user?.role) && user?.role !== USER_ROLES.SALES,
    },
    {
      name: 'Mi Cuadrante',
      href: '/my-timesheet',
      icon: FiCalendar,
      show: ![USER_ROLES.CLEANING_STAFF, USER_ROLES.SALES].includes(user?.role),
    },
    {
      name: 'Configuración',
      href: '/configuration',
      icon: FiSettings,
      show: canManageRooms(user?.role) && user?.role !== USER_ROLES.SALES,
    },
    {
      name: 'Establecimiento',
      href: '/establishment-settings',
      icon: FiGrid,
      show: canManageEstablishmentSettings(user?.role) && user?.role !== USER_ROLES.SALES,
    },
    {
      name: 'Mi Suscripción',
      href: '/subscription',
      icon: FiCreditCard,
      show: canManageSubscription(user?.role) && user?.role !== USER_ROLES.SALES,
    },
    // Items adicionales
    {
      name: 'Gestión de Usuarios',
      href: '/users',
      icon: FiUsers,
      show: canManageUsers(user?.role),
    },
    {
      name: 'Reportes',
      href: '/reports',
      icon: FiBarChart2,
      show: canViewReports(user?.role) && user?.role !== USER_ROLES.SALES,
    },
  ];

  const filteredNavigation = navigationItems.filter(item => item.show);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      {/* Sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-all duration-300 ease-in-out',
          {
            'translate-x-0': isOpen || !isMobile,
            '-translate-x-full': !isOpen && isMobile,
            'w-64': !isEffectivelyCollapsed,
            'w-20': isEffectivelyCollapsed,
          }
        )}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <h2 className={clsx('text-lg font-semibold text-gray-900 whitespace-nowrap overflow-hidden transition-all duration-300', {
            'opacity-0 w-0': isEffectivelyCollapsed,
            'opacity-100 w-auto': !isEffectivelyCollapsed,
          })}>
            Menu
          </h2>
          <div className="flex items-center gap-2">
            {!isMobile && (
              <button
                onClick={handleToggleCollapse}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                aria-label={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
                title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
              >
                {isCollapsed ? (
                  <FiChevronRight className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <FiChevronLeft className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            )}
            {isMobile && (
              <button
                onClick={onClose}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Cerrar menú"
              >
                <FiX className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-4 pb-32">
          <ul className="space-y-2">
            {filteredNavigation.map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  onClick={() => isMobile && onClose()}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 group relative',
                      {
                        'bg-blue-100 text-blue-700 border-r-2 border-blue-700': isActive,
                        'text-gray-700 hover:bg-gray-100 hover:text-gray-900': !isActive,
                        'justify-center': isEffectivelyCollapsed,
                      }
                    )
                  }
                >
                  <item.icon className="h-5 w-5 mr-3 flex-shrink-0" aria-hidden="true" />
                  <span className={clsx('whitespace-nowrap overflow-hidden transition-all duration-300', {
                    'opacity-0 w-0': isEffectivelyCollapsed,
                    'opacity-100 w-auto': !isEffectivelyCollapsed,
                  })}>
                    {item.name}
                  </span>

                  {/* Tooltip on hover when collapsed */}
                  {isEffectivelyCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                      {item.name}
                    </div>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout button - positioned above user info */}
        <div className="absolute bottom-20 left-0 right-0 px-4 pt-4 border-t border-gray-200 bg-white">
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className={clsx(
              'flex items-center w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200 group relative',
              { 'justify-center': isEffectivelyCollapsed }
            )}
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
          >
            <FiLogOut className="h-5 w-5 mr-3 flex-shrink-0" aria-hidden="true" />
            <span className={clsx('whitespace-nowrap overflow-hidden transition-all duration-300', {
              'opacity-0 w-0': isEffectivelyCollapsed,
              'opacity-100 w-auto': !isEffectivelyCollapsed,
            })}>
              Cerrar sesión
            </span>

            {/* Tooltip when collapsed */}
            {isEffectivelyCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                Cerrar sesión
              </div>
            )}
          </button>
        </div>

        {/* User info in sidebar */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-gray-50">
          <div className={clsx('flex items-center min-w-0', { 'justify-center': isEffectivelyCollapsed })}>
            <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 group relative">
              <span className="text-xs font-medium text-white">
                {user?.name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </span>

              {/* User info tooltip when collapsed */}
              {isEffectivelyCollapsed && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 min-w-max">
                  <p className="font-medium">{user?.name || user?.username}</p>
                  <p className="text-gray-300">{user?.establishment?.name || 'Sin establecimiento'}</p>
                </div>
              )}
            </div>
            <div className={clsx('ml-3 min-w-0 flex-1 overflow-hidden transition-all duration-300', {
              'opacity-0 w-0': isEffectivelyCollapsed,
              'opacity-100 w-auto': !isEffectivelyCollapsed,
            })}>
              <p className="text-xs font-medium text-gray-700 truncate whitespace-nowrap">
                {user?.name || user?.username}
              </p>
              <p className="text-xs text-gray-500 truncate whitespace-nowrap">
                {user?.establishment?.name || 'Sin establecimiento'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      {showLogoutConfirm && (
        <ConfirmDialog
          title="Cerrar sesión"
          message="¿Estás seguro de que deseas cerrar sesión?"
          confirmText="Cerrar sesión"
          confirmStyle="danger"
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </>
  );
};

export default Sidebar;